// layerLoadingUtils.ts - Pure functions for layer management

// Types
import { shapeToVectorLayer } from '@ais-ui/react-openlayers';
import { areSourceIdentifiersShowOnStartUp } from '../Coverage/areSourceIdentifiersShownOnStartUp';
import { isShapeLabelShown } from '../../Shapes/isShapeShownOnStartUp';
import { getFilters, Settings } from '@ais-ui/websockets';
import { isFilterApplied } from '../../Filters/utils';

export interface LayerConfig {
  type: string;
  id: string;
  enabled: boolean;
}

export interface LayerLoadingDependencies {
  settings: any;
  enabledWmsLayers: any[];
  sourceIdentifiers: any[];
  doesLayerExist: (id: string) => boolean;
  shapeFetch: (id: string) => Promise<any>;
  addLayer: (...args: any[]) => any;
  addSourceIdentifiersToMap: (...args: any[]) => void;
  addExistingLayerItem: (...args: any[]) => void;
  onWmsToTileLayerWithLayerItem: (...args: any[]) => Promise<any>;
  removeShapeFromFavorites: (id: string) => void;
  toast: (...args: any[]) => void;
  olSession: any;
  filterContext: any;
}

// Create layer ordering from current favorites
export const createLayerOrderingFromFavorites = (
  settings: Settings,
  enabledWmsLayers: any[]
): LayerConfig[] => {
  const layerOrdering: LayerConfig[] = [];

  // Always include ships first (at the bottom)
  layerOrdering.push({ type: 'ships', id: 'ships-layer', enabled: true });

  // Add favorite shapes (below ships by default)
  const favoriteShapes =
    settings['ribbonPlugin.settings']?.favorite_shapes?.shapes || [];
  favoriteShapes.forEach((shapeId: string) => {
    layerOrdering.push({ type: 'shape', id: shapeId, enabled: true });
  });

  // Add coverage if enabled on startup
  if (areSourceIdentifiersShowOnStartUp(settings)) {
    layerOrdering.push({
      type: 'coverage',
      id: 'source-identifiers',
      enabled: true,
    });
  }

  // Add enabled WMS layers
  enabledWmsLayers.forEach((wmsLayer) => {
    layerOrdering.push({
      type: 'wms',
      id: wmsLayer.wmsLayerInfo.id,
      enabled: true,
    });
  });

  return layerOrdering;
};

// Determine position based on layer order relative to ships
export const determineLayerPosition = (
  layerIndex: number,
  layerOrdering: LayerConfig[]
): 'above-ships' | 'below-ships' => {
  const shipsIndex = layerOrdering.findIndex((l) => l.type === 'ships');
  return shipsIndex !== -1 && layerIndex < shipsIndex
    ? 'below-ships'
    : 'above-ships';
};

// Load a single layer by type
export const loadSingleLayerByType = async (
  layerConfig: LayerConfig,
  position: 'above-ships' | 'below-ships' | 'top',
  deps: LayerLoadingDependencies
): Promise<void> => {
  if (!layerConfig.enabled || deps.doesLayerExist(layerConfig.id)) {
    return;
  }

  try {
    switch (layerConfig.type) {
      case 'shape':
        await loadShapeLayer(layerConfig.id, position, deps);
        break;
      case 'coverage':
        await loadCoverageLayer(layerConfig.id, position, deps);
        break;
      case 'wms':
        await loadWmsLayer(layerConfig.id, position, deps);
        break;
      case 'ships':
        // Ships layer is handled elsewhere
        break;
      default:
        console.warn(`Unknown layer type: ${layerConfig.type}`);
    }
  } catch (error) {
    deps.toast({
      className: 'load-layer-error-toast',
      title: 'Error!',
      description: `Failed to load layer ${layerConfig.id}`,
      variant: 'destructive',
    });
  }
};

// Individual layer type loaders
const loadShapeLayer = async (
  shapeId: string,
  position: 'above-ships' | 'below-ships' | 'top',
  deps: LayerLoadingDependencies
): Promise<void> => {
  try {
    const shape = await deps.shapeFetch(shapeId);
    const layer = shapeToVectorLayer(shape);
    const name = shape?.properties?.name || 'No name';

    deps.addLayer(
      layer,
      name,
      true,
      true,
      true,
      shape.properties,
      shape.properties.id,
      isShapeLabelShown(deps.settings, shapeId),
      undefined,
      undefined,
      position
    );
  } catch (error) {
    deps.removeShapeFromFavorites(shapeId);
    deps.toast({
      className: 'enable-shape-layer-error-toast',
      title: 'Error!',
      description: `Failed to enable layer ${shapeId}`,
      variant: 'destructive',
    });
  }
};

const loadCoverageLayer = async (
  layerId: string,
  position: 'above-ships' | 'below-ships' | 'top',
  deps: LayerLoadingDependencies
): Promise<void> => {
  if (layerId !== 'source-identifiers') {
    return;
  }

  // Check if coverage should be shown based on settings
  if (!areSourceIdentifiersShowOnStartUp(deps.settings)) {
    console.log('Coverage layer not enabled in settings');
    return;
  }

  // No source identifiers available - just skip
  if (!deps.sourceIdentifiers || deps.sourceIdentifiers.length === 0) {
    console.log('No source identifiers available, skipping coverage layer');
    return;
  }

  // Add the coverage layer
  deps.addSourceIdentifiersToMap(
    (
      layer: any,
      name: string,
      isRemovable: boolean,
      canZoom: boolean,
      canShowLabel: boolean,
      properties: any,
      id: string,
      showLabel: boolean
    ) => {
      return deps.addLayer(
        layer,
        name,
        isRemovable,
        canZoom,
        canShowLabel,
        properties,
        id,
        showLabel,
        undefined,
        undefined,
        position
      );
    },
    deps.sourceIdentifiers
  );
};

const loadWmsLayer = async (
  layerId: string,
  position: 'above-ships' | 'below-ships' | 'top',
  deps: LayerLoadingDependencies
): Promise<void> => {
  const enabledLayer = deps.enabledWmsLayers.find(
    (wms) => wms.wmsLayerInfo.id === layerId
  );

  if (!enabledLayer) return;

  try {
    const { tileLayer, layerItem } = await deps.onWmsToTileLayerWithLayerItem(
      enabledLayer.wmsInfo,
      enabledLayer.wmsLayerInfo
    );

    if (!tileLayer || !layerItem) {
      deps.toast({
        className: 'enable-wms-layer-error-toast',
        title: 'Error!',
        description: `WMS layer ${enabledLayer.wmsLayerInfo.title} is invalid`,
        variant: 'destructive',
      });
      return;
    }

    deps.addExistingLayerItem(tileLayer, layerItem, position);
  } catch (error) {
    deps.toast({
      className: 'enable-wms-layer-error-toast',
      title: 'Error!',
      description: `Failed to enable WMS layer ${layerId}`,
      variant: 'destructive',
    });
  }
};

// Load favorite filters
export const loadFavoriteFilters = async (
  favoriteFilters: string[],
  olSession: any,
  filterContext: any,
  toast: any
): Promise<void> => {
  if (!favoriteFilters || favoriteFilters.length === 0) return;

  try {
    const allFilters = await getFilters(olSession);

    if (!allFilters) {
      toast({
        className: 'fetch-filters-error-toast',
        title: 'Error!',
        description: 'Failed to fetch filters',
        variant: 'destructive',
      });
      return;
    }

    const favoriteFilterObjects = allFilters.filter((filter: any) =>
      favoriteFilters.includes(filter.uuid)
    );

    const filtersToAdd = favoriteFilterObjects.filter(
      (filter: any) => !isFilterApplied(filter, filterContext.customFilters)
    );

    if (filtersToAdd.length > 0) {
      filterContext.setCustomFilters([
        ...filterContext.customFilters,
        ...filtersToAdd,
      ]);
    }
  } catch (error) {
    toast({
      className: 'apply-favorite-filters-error-toast',
      title: 'Error!',
      description: 'Failed to apply favorite filters',
      variant: 'destructive',
    });
  }
};
